/**
 * API utility functions for handling API calls with timeouts and retries
 */

/**
 * Retry a function with exponential backoff
 * Useful for handling transient network errors and server issues
 *
 * @param {Function} fn - Async function to retry
 * @param {number} maxRetries - Maximum number of retry attempts (default: 2)
 * @param {number} delay - Initial delay in milliseconds (default: 1000)
 * @returns {Promise} Result of the function
 * @throws {Error} Throws the last error if all retries fail
 *
 * @example
 * // Retry an API call up to 2 times with exponential backoff
 * await retryWithBackoff(
 *   async () => axios.get('/api/data'),
 *   2,  // maxRetries
 *   1000  // initial delay (1s, then 2s)
 * );
 */
export async function retryWithBackoff(fn, maxRetries = 2, delay = 1000) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      // Don't retry on last attempt
      if (i === maxRetries - 1) throw error;

      // Don't retry on authentication errors (401)
      if (error.response?.status === 401) throw error;

      // Calculate exponential backoff delay: delay * 2^i
      // i=0: delay * 1 = 1000ms
      // i=1: delay * 2 = 2000ms
      const backoffDelay = delay * Math.pow(2, i);

      await new Promise(resolve => setTimeout(resolve, backoffDelay));
    }
  }
}

/**
 * Attempt an API call once with a timeout (fail-fast approach)
 * Used during splash screen initialization to prevent blocking page load
 * Can be used for Vuex dispatch calls or axios calls
 * @param {string} actionName - Name of the action being called (for logging)
 * @param {Function} actionDispatch - Function that returns a Promise (usually a Vuex dispatch or axios call)
 * @param {number} timeoutMs - Timeout in milliseconds (default: 3000ms)
 * @returns {Promise<{status: 'fulfilled'|'rejected', value?: any, reason?: Error, actionName: string}>}
 */
export async function attemptCallOnce(actionName, actionDispatch, timeoutMs = 3000) {
  try {
    // Set a timeout to fail fast
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`Timeout: ${actionName} took too long`)), timeoutMs);
    });

    const result = await Promise.race([actionDispatch(), timeoutPromise]);

    return { status: 'fulfilled', value: result, actionName };
  } catch (error) {
    // Return error immediately without retrying
    return { status: 'rejected', reason: error, actionName };
  }
}

/**
 * Wraps a promise with a timeout, automatically cleaning up the timeout if the promise resolves/rejects early
 * Used during splash screen initialization to add individual timeouts to API calls
 * @param {Promise} promise - The promise to wrap with a timeout
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} actionName - Name of the action being called (for logging)
 * @returns {Promise} The promise wrapped with timeout logic
 */
export function withTimeout(promise, timeoutMs, actionName) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      console.warn(`[App] ${actionName} timed out after ${timeoutMs}ms`);
      reject(new Error(`${actionName} timeout after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  return Promise.race([
    promise.finally(() => {
      // Clear timeout if promise resolves/rejects before timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }),
    timeoutPromise,
  ]);
}

/**
 * Timeout constants (in milliseconds)
 */
export const API_CALL_TIMEOUT = 7000; // 7 seconds per individual API call
export const INITIALIZATION_TIMEOUT = 10000; // 10 seconds for overall initialization

/**
 * Standard API action names for homepage initialization (above-fold only)
 * Below-fold APIs (getJustForYou, getFeaturedChurchUpdates) are lazy-loaded via IntersectionObserver on the homepage
 */
export const HOMEPAGE_ACTION_NAMES = [
  'getMiddleNavIcons',
  'getFeaturedCards',
  'getUpdates',
  'getAllAnimations',
];

/**
 * Critical API indices - these APIs must succeed for the app to function
 * Indices correspond to: getMiddleNavIcons (0), getFeaturedCards (1), getAllAnimations (3)
 */
export const CRITICAL_API_INDICES = [0, 1, 3];

/**
 * Creates dispatch functions for failed API calls (for background retry)
 * @param {string} actionName - Name of the action
 * @param {Object} store - Vuex store instance
 * @returns {Function} Dispatch function for retrying the action
 */
function createRetryDispatch(actionName, store) {
  switch (actionName) {
    case 'getMiddleNavIcons':
      return () => store.dispatch('homepage/getMiddleNavIcons');
    case 'getFeaturedCards':
      return () => store.dispatch('homepage/getFeaturedCards');
    case 'getUpdates':
      return () => store.dispatch('updates/getUpdates', 'all');
    case 'getAllAnimations':
      return () => store.dispatch('homepage/getAllAnimations');
    default:
      return () => Promise.resolve();
  }
}

/**
 * Executes multiple API calls in parallel with individual timeouts
 * @param {Array<string>} actionNames - Array of action names
 * @param {Array<Function>} dispatchFunctions - Array of dispatch functions corresponding to actionNames
 * @param {number} timeoutMs - Timeout in milliseconds for each API call
 * @returns {Promise<Array>} Array of Promise.allSettled results
 */
export async function executeApiCallsWithTimeouts(actionNames, dispatchFunctions, timeoutMs) {
  const apiCallStartTimes = actionNames.map(() => Date.now());

  const dataLoadResults = await Promise.allSettled(
    dispatchFunctions.map((dispatch, index) =>
      withTimeout(dispatch(), timeoutMs, actionNames[index]),
    ),
  );

  return { dataLoadResults, apiCallStartTimes };
}

/**
 * Processes API call results: logs timing, tracks failures, checks for 401 errors
 * @param {Array} dataLoadResults - Results from Promise.allSettled
 * @param {Array<string>} actionNames - Array of action names
 * @param {Array<number>} apiCallStartTimes - Array of start times for each API call
 * @param {Object} store - Vuex store instance (for creating retry dispatches)
 * @param {boolean} trackFailedCalls - Whether to track failed calls for background retry
 * @returns {Object} Object containing failedCalls array and has401Error boolean
 */
export function processApiResults(
  dataLoadResults,
  actionNames,
  apiCallStartTimes,
  store,
  trackFailedCalls = false,
) {
  const failedCalls = [];

  // Log results with timing and error details
  dataLoadResults.forEach((result, index) => {
    const duration = Date.now() - apiCallStartTimes[index];
    const actionName = actionNames[index];

    if (result.status === 'fulfilled') {
      console.log(`[App] ${actionName} completed in ${duration}ms`);
    } else {
      const error = result.reason;

      // Track failed calls (except 401 errors which should not be retried)
      if (trackFailedCalls && (!error?.response || error.response.status !== 401)) {
        failedCalls.push({
          actionName,
          dispatch: createRetryDispatch(actionName, store),
          error,
        });
      }

      // Don't log 401 errors as they'll be handled globally
      if (!error?.response || error.response.status !== 401) {
        console.warn(
          `[App] ${actionName} failed after ${duration}ms${
            trackFailedCalls ? ' (will retry in background)' : ''
          }:`,
          {
            error: error?.message || 'Unknown error',
            status: error?.response?.status,
            statusText: error?.response?.statusText,
            url: error?.config?.url,
          },
        );
      } else {
        console.error(`[App] ${actionName} failed with 401 (Unauthorized) after ${duration}ms`);
      }
    }
  });

  // Check for 401 errors
  const has401Error = dataLoadResults.some(result => {
    if (result.status === 'rejected') {
      return result.reason?.response?.status === 401;
    }
    return false;
  });

  return { failedCalls, has401Error };
}

/**
 * Verifies that critical APIs succeeded
 * @param {Array} dataLoadResults - Results from Promise.allSettled
 * @param {Array<number>} criticalIndices - Indices of critical APIs (default: CRITICAL_API_INDICES)
 * @returns {Object} Object containing criticalSuccessCount and whether to throw error
 */
export function verifyCriticalApis(dataLoadResults, criticalIndices = CRITICAL_API_INDICES) {
  const criticalResults = criticalIndices.map(idx => dataLoadResults[idx]);
  const criticalSuccessCount = criticalResults.filter(r => r.status === 'fulfilled').length;

  if (criticalSuccessCount === 0) {
    console.warn('[App] No critical APIs succeeded - continuing with degraded home state');
    return {
      criticalSuccessCount: 0,
      shouldThrow: false,
      degraded: true,
      error: new Error('No critical APIs succeeded - initialization failed'),
    };
  }

  return {
    criticalSuccessCount,
    shouldThrow: false,
    degraded: false,
  };
}

/**
 * Checks if critical homepage data exists in the store
 * @param {Object} store - Vuex store instance
 * @returns {Object} Object containing boolean flags for each critical data type
 */
export function checkStoreData(store) {
  const hasMiddleNavIcons =
    store.state.homepage?.middleNavIcons &&
    Array.isArray(store.state.homepage.middleNavIcons) &&
    store.state.homepage.middleNavIcons.length > 0;
  const hasFeaturedCards =
    store.state.homepage?.featuredCards &&
    Array.isArray(store.state.homepage.featuredCards) &&
    store.state.homepage.featuredCards.length > 0;
  const hasAnimations =
    store.state.homepage?.allAnimations &&
    Array.isArray(store.state.homepage.allAnimations) &&
    store.state.homepage.allAnimations.length > 0;
  const hasOnboardStatus = store.state.profile?.hasRegistered !== undefined;

  return {
    hasMiddleNavIcons,
    hasFeaturedCards,
    hasAnimations,
    hasOnboardStatus,
  };
}

const POST_INIT_EXCLUDED_PATHS = ['/', '/pre-sign-in', '/login', '/pre-loading'];
const INVALID_ROUTE_PATH_NAMES = new Set(['undefined', 'null']);

function isPreservableRoutePath(path) {
  if (typeof path !== 'string') {
    return false;
  }

  const trimmedPath = path.trim();
  const pathName = trimmedPath.replace(/^\/+|\/+$/g, '').toLowerCase();
  return (
    trimmedPath.startsWith('/') &&
    !POST_INIT_EXCLUDED_PATHS.includes(trimmedPath) &&
    !INVALID_ROUTE_PATH_NAMES.has(pathName)
  );
}

export function preserveRouteOrFallback(route, fallback) {
  if (isPreservableRoutePath(route?.path)) {
    return { path: route.path.trim(), query: route.query || {}, hash: route.hash || '' };
  }
  return fallback;
}

export function determineTargetPath(onboardStatusSuccess, store, route, onboardingStatusConstant) {
  if (onboardStatusSuccess && store.state.profile.hasRegistered !== onboardingStatusConstant) {
    return '/register';
  }

  return preserveRouteOrFallback(route, '/tabs/home');
}
